// Libraries
import { useState, useEffect, useRef } from "react";
import * as d3 from 'd3';

// Components
import Main from '../components/Main';
import { QueryMenu } from "../components/Query";
import Map from "../components/Map";
import * as Tooltip from "../components/Tooltip";
import Status from "../components/Status";
import { ChangeYear } from "../utils/Types";
import { BrowserMessage } from '../components/BrowserMessage';
import { Loader, partOfSelectedState, partOfSelectedCounty } from '../utils/Helper';
import { setFiltersRange } from "../components/Filters";
import OverlayScreen from '../components/OverlayScreen';

// Data
import { selectVariable, defaultCounty, defaultState, defaultMap } from "../utils/Global";
import { getCounties, getTracts, getVd, dmCountyLongData, dmTractLongData, dmPollingLocsLongData } from "../utils/DM";
import { fetchData, fetchGeoData, fetchStatesGeoData } from "../utils/Fetch";

// Types
import { PollingLoc } from "../utils/Types";

export default function Home({initialized}: {initialized: boolean}): JSX.Element {

    const [selectedState, setSelectedState] = useState(defaultState);
    const [selectedCounty, setSelectedCounty] = useState(defaultCounty);
    const [changeYearOpts, setChangeYearOpts] = useState<ChangeYear[]>(selectVariable.changeYear);
    const [changeYear, setChangeYear] = useState(changeYearOpts[0]);
    const [equityIndicator, setEquityIndicator] = useState(selectVariable.equityIndicator[0]);
    const [indicator, setIndicator] = useState(selectVariable.indicator[0]);
    const [showPolls, setShowPolls] = useState(true);
    const [showVD, setShowVD] = useState(false);
    const [showEquityIndicator, setShowEquityIndicator] = useState(false);
    const [showCensusTracts, setShowCensusTracts] = useState(true);
    // const [isFullScreen, setFullScreen] = useState(true);
    const [geoFocus, setGeoFocus] = useState<string>('US');
    const [pollHover, setPollHover] = useState({});
    const [geoHover, setGeoHover] = useState({});
    const [zoom, setZoom] = useState<number>(defaultMap.zoom);
    const [visualizationLoading, setVisualizationLoading] = useState(false);

    // Data loading
    const [loadedStatesGeoData, setLoadedStatesGeoData] = useState<boolean>(false);
    const [loadedCountiesLongData, setLoadedCountiesLongData] = useState<boolean>(false);
    const [loadedCountiesGeoData, setLoadedCountiesGeoData] = useState<boolean>(false);
    const [loadedCountiesData, setLoadedCountiesData] = useState<boolean>(false);
    const [loadedCountiesDataTimes, setLoadedCountiesDataTimes] = useState<number>(0);
    const [loadedTractsLongData, setLoadedTractsLongData] = useState<boolean>(false);
    const [loadedTractsGeoData, setLoadedTractsGeoData] = useState<boolean>(false);
    const [loadedTractsData, setLoadedTractsData] = useState<boolean>(false);
    const [loadedTractsDataTimes, setLoadedTractsDataTimes] = useState<number>(0);
    const [loadedPollingLocsLongData, setLoadedPollingLocsLongData] = useState<boolean>(false);
    const [loadedPollingLocsData, setLoadedPollingLocsData] = useState<boolean>(false);
    const [loadedVdGeoData, setLoadedVdGeoData] = useState<boolean>(false);
    const [loadedVdData, setLoadedVdData] = useState<boolean>(false);

    // Set data
    const [statesGeoData, setStatesGeoData] = useState<GeoJSON.FeatureCollection>({} as GeoJSON.FeatureCollection);
    const [countiesLongData, setCountiesLongData] = useState<any[]>([]);
    const [countiesGeoData, setCountiesGeoData] = useState<any[]>([]);
    const [countiesData, setCountiesData] = useState<GeoJSON.FeatureCollection>({} as GeoJSON.FeatureCollection);
    const [tractsLongData, setTractLongData] = useState<any[]>([]);
    const [tractGeoData, setTractGeoData] = useState<any[]>([]);
    const [tractsData, setTractsData] = useState<GeoJSON.FeatureCollection>({} as GeoJSON.FeatureCollection);
    const [pollingLocsLongData, setPollingLocsLongData] = useState<any[]>([]);
    const [pollingLocsData, setPollingLocsData] = useState<PollingLoc[]>([]);
    const [vdGeoData, setVdGeoData] = useState<any[]>([]);
    const [vdData, setVdData] = useState<GeoJSON.FeatureCollection>({} as GeoJSON.FeatureCollection);

    // County Level Filters -------------------------------------------------------------------------------

        // Summary Indicators
    const [changeNoPollsCountyFilter, setChangeNoPollsCountyFilter] = useState<number[]>([0, 0]);
    const [changeNoPollsRangeCountyMarks, setChangeNoPollsRangeCountyMarks] = useState<any[]>([0, 0]);
    const [overallChangeCountyFilter, setOverallChangeCountyFilter] = useState<number[]>([0, 0]);
    const [overallChangeRangeCountyMarks, setOverallChangeRangeCountyMarks] = useState<any[]>([0, 0]);

        // Per Capita Indicators
    const [changeYearCVAPPerPollCountyFilter, setChangeYearCVAPPerPollCountyFilter] = useState<number[]>([0, 0]);
    const [changeYearCVAPPerPollCountyMarks, setChangeYearCVAPPerPollCountyMarks] = useState<any[]>([0, 0]); 
    const [baseYearCVAPPerPollCountyFilter, setBaseYearCVAPPerPollCountyFilter] = useState<number[]>([0, 0]);
    const [baseYearCVAPPerPollCountyMarks, setBaseYearCVAPPerPollCountyMarks] = useState<any[]>([0, 0]); 

        // Equity Indicators
    const [pctBlackCVAPCountyFilter, setPctBlackCVAPCountyFilter] = useState<number[]>([0, 0]);
    const [pctBlackCVAPRangeCountyMarks, setPctBlackCVAPRangeCountyMarks] = useState<any[]>([0, 0]);
    const [pctHispanicCVAPCountyFilter, setPctHispanicCVAPCountyFilter] = useState<number[]>([0, 0]);
    const [pctHispanicCVAPRangeCountyMarks, setPctHispanicCVAPRangeCountyMarks] = useState<any[]>([0, 0]);
    const [popDensityCountyFilter, setPopDensityCountyFilter] = useState<number[]>([0, 0]);
    const [popDensityRangeCountyMarks, setPopDensityRangeCountyMarks] = useState<any[]>([0, 0]);


    // Tract Level Filters -------------------------------------------------------------------------------
        // Equity Indicators
    const [pctBlackCVAPTractFilter, setPctBlackCVAPTractFilter] = useState<number[]>([0, 0]);
    const [pctBlackCVAPRangeTractMarks, setPctBlackCVAPRangeTractMarks] = useState<any[]>([0, 0]);
    const [pctHispanicCVAPTractFilter, setPctHispanicCVAPTractFilter] = useState<number[]>([0, 0]);
    const [pctHispanicCVAPRangeTractMarks, setPctHispanicCVAPRangeTractMarks] = useState<any[]>([0, 0]);
    const [popDensityTractFilter, setPopDensityTractFilter] = useState<number[]>([0, 0]);
    const [popDensityRangeTractMarks, setPopDensityRangeTractMarks] = useState<any[]>([0, 0]);

    // Per Summary Indicators
    const [changeNoPollsTractFilter, setChangeNoPollsTractFilter] = useState<number[]>([0, 0]);
    const [changeNoPollsRangeTractMarks, setChangeNoPollsRangeTractMarks] = useState<any[]>([0, 0]);
    const [overallChangeTractFilter, setOverallChangeTractFilter] = useState<number[]>([0, 0]);
    const [overallChangeRangeTractMarks, setOverallChangeRangeTractMarks] = useState<any[]>([0, 0]);

    // Set GeoJson Data
    const [geoJsonData, setGeoJsonData] = useState<GeoJSON.FeatureCollection>({} as GeoJSON.FeatureCollection);
    const [geoJsonBoundaryData, setGeoJsonBoundaryData] = useState<GeoJSON.FeatureCollection>({} as GeoJSON.FeatureCollection);
    const [geoJsonVdData, setGeoJsonVdData] = useState<GeoJSON.FeatureCollection>({} as GeoJSON.FeatureCollection);
    const [pointsData, setPointsData] = useState<any[]>([]);

    const [loadedGeoJsonData, setLoadedGeoJsonData] = useState<boolean>(false);
    const [loadedGeoJsonBoundaryData, setLoadedGeoJsonBoundaryData] = useState<boolean>(false);
    const [loadedPointsData, setLoadedPointsData] = useState<boolean>(false);

    const mapRef = useRef<L.Map | null>(null);

    // React Hooks --------------------------------------------------------------------------
    useEffect(()=>{
        Tooltip.init();
        fetchStatesGeoData('statesGeoJSON', setStatesGeoData, setLoadedStatesGeoData);
        fetchGeoData('countiesGeoJSON', setCountiesGeoData, setLoadedCountiesGeoData);
        fetchGeoData('tractsGeoJSON', setTractGeoData, setLoadedTractsGeoData);
        fetchGeoData('votingDistrictsGeoJSON', setVdGeoData, setLoadedVdGeoData);

        d3.select('body').style('overflow-y', 'hidden');
    }, []);

    if (mapRef.current !== null) {
        (mapRef.current as L.Map)
            .on('zoomend', () => {
                setZoom((mapRef.current as L.Map).getZoom());
        });
    }

    useEffect(() => {
        if (zoom <= 5) {
            setGeoFocus('US');
        } else if (zoom > 5 && zoom < 9) {
            setGeoFocus('State');
            const state = selectedState;
            state.zoom = zoom;
            setSelectedState(state);
        } else if (zoom >= 9) {
            setGeoFocus('County');
            const county = selectedCounty;
            county.zoom = zoom;
            setSelectedCounty(county);
        }
    }, [zoom]);

    useEffect(() => {

        if (geoFocus === "US") {
            setSelectedState(defaultState);
            setSelectedCounty(defaultCounty);
            if (mapRef.current !== null) {
                (mapRef.current as L.Map).scrollWheelZoom.disable();
            }
        } else {
            if (mapRef.current !== null) {
                (mapRef.current as L.Map).scrollWheelZoom.enable();
            }
        }

        if ((geoFocus === "US" || geoFocus === "State")) {
            setShowVD(false);
            setShowCensusTracts(true);
            setShowPolls(false);
        } else {
            setShowPolls(true);
            setIndicator(selectVariable.indicator[0]);
        }

    }, [geoFocus]);

    useEffect(() => {

        if (geoFocus === "State" || geoFocus === "County") {
            fetchData("county_summary", countiesLongData, setCountiesLongData, dmCountyLongData, setLoadedCountiesLongData, changeYear);
            fetchData("tract_summary", tractsLongData, setTractLongData, dmTractLongData, setLoadedTractsLongData, changeYear);
        }

        if (geoFocus === "County") {
            fetchData("polling_locations_summary", pollingLocsLongData, setPollingLocsLongData, dmPollingLocsLongData, setLoadedPollingLocsLongData, changeYear);
        }

    }, [geoFocus, changeYear]);

    useEffect(()=>{
        setLoadedCountiesData(false);
        if (loadedCountiesGeoData && loadedCountiesLongData) {
            if (countiesLongData.find(d => d.changeYear === changeYear.changeYear) !== undefined) {
                setCountiesData(getCounties(countiesGeoData, countiesLongData.find(d => d.changeYear === changeYear.changeYear).data));
            }
            setLoadedCountiesData(true);
            setLoadedCountiesDataTimes(loadedCountiesDataTimes + 1);
        }
    }, [loadedCountiesGeoData, loadedCountiesLongData, countiesLongData, changeYear]);

    useEffect(() => {

        let decennialCensusYear: number;
        if (changeYear.baseYear < 2020) {
            decennialCensusYear = 2010;
        } else {
            decennialCensusYear = 2020;
        }

        setLoadedTractsData(false);
        if (loadedTractsGeoData && loadedTractsLongData) {
            if (tractsLongData.find(d => d.changeYear === changeYear.changeYear) !== undefined) {
                setTractsData(getTracts(tractGeoData.filter((d: any) => d.baseYear === decennialCensusYear), tractsLongData.find(d => d.changeYear === changeYear.changeYear).data));
            }
            setLoadedTractsData(true);
            setLoadedTractsDataTimes(loadedTractsDataTimes + 1);
        }
    }, [loadedTractsGeoData, loadedTractsLongData, tractsLongData, changeYear]);

    useEffect(()=>{
        setLoadedPollingLocsData(false);
        if (loadedPollingLocsLongData) {
            if (pollingLocsLongData.find(d => d.changeYear === changeYear.changeYear) !== undefined) {
                setPollingLocsData(pollingLocsLongData.find(d => d.changeYear === changeYear.changeYear).data);
            }
            setLoadedPollingLocsData(true);
        }
    }, [loadedPollingLocsLongData, pollingLocsLongData, changeYear]);

    useEffect(()=>{
        setLoadedVdData(false);
        if (loadedVdGeoData) {
            setVdData(getVd(vdGeoData));
            setLoadedVdData(true);
        }
    }, [loadedVdGeoData, changeYear]);

    useEffect(()=>{
        if (selectedState.stabbr !== '') {
            const opts = selectVariable.changeYear.filter((d: any) => d[selectedState.stabbr]);
            const optsContainsCurrentChangeYear = opts.filter(d => d.changeYear === changeYear.changeYear);
            const newChangeYear = optsContainsCurrentChangeYear.length > 0? optsContainsCurrentChangeYear[0]: opts[0];
            setChangeYearOpts(opts);
            setChangeYear(newChangeYear);
        }
    }, [selectedState]);

    useEffect(()=>{

        if (loadedCountiesData && loadedCountiesDataTimes === 1) {
            const df = countiesData?.features.filter((d: any) => d.properties.stfp === selectedState.stfp);

            setFiltersRange(df, setPctBlackCVAPRangeCountyMarks, setPctBlackCVAPCountyFilter, "baseYearPctCVAPBlack", false, true, 5);
            setFiltersRange(df, setPctHispanicCVAPRangeCountyMarks, setPctHispanicCVAPCountyFilter, "baseYearPctCVAPHispanic", false, true);
            setFiltersRange(df, setPopDensityRangeCountyMarks, setPopDensityCountyFilter, "baseYearPopDensity", false, false);
           
            setFiltersRange(df, setBaseYearCVAPPerPollCountyMarks, setBaseYearCVAPPerPollCountyFilter, "baseYearCVAPPerPoll", true, false, 50);
            setFiltersRange(df, setChangeYearCVAPPerPollCountyMarks, setChangeYearCVAPPerPollCountyFilter, "changeYearCVAPPerPoll", true, false, 50);
            setFiltersRange(df, setChangeNoPollsRangeCountyMarks, setChangeNoPollsCountyFilter, "changeNoPolls", true, false);
            setFiltersRange(df, setOverallChangeRangeCountyMarks, setOverallChangeCountyFilter, "overallChange", true, false);
        }

    }, [selectedState, loadedCountiesData, loadedCountiesDataTimes]);

    useEffect(()=>{

        if (loadedTractsData && loadedTractsDataTimes === 1) {

            const df = tractsData?.features.filter((d: any) => d.properties.stfp === selectedState.stfp);
            setFiltersRange(df, setPctBlackCVAPRangeTractMarks, setPctBlackCVAPTractFilter, "baseYearPctCVAPBlack", false, true, 5);
            setFiltersRange(df, setPctHispanicCVAPRangeTractMarks, setPctHispanicCVAPTractFilter, "baseYearPctCVAPHispanic", false, true);
            setFiltersRange(df, setPopDensityRangeTractMarks, setPopDensityTractFilter, "baseYearPopDensity", false, false);

            setFiltersRange(df, setChangeNoPollsRangeTractMarks, setChangeNoPollsTractFilter, "changeNoPolls", true, false);
            setFiltersRange(df, setOverallChangeRangeTractMarks, setOverallChangeTractFilter, "overallChange", true, false);
        }

    }, [selectedState, loadedTractsData, loadedTractsDataTimes]);
   
    useEffect(() => {

        let featuresData: GeoJSON.FeatureCollection = {
            type: "FeatureCollection",
            features: [] as GeoJSON.Feature[]
        };
    
        let boundaryData: GeoJSON.FeatureCollection = {
            type: "FeatureCollection",
            features: [] as GeoJSON.Feature[]
        };

        let vdfc: GeoJSON.FeatureCollection = {
            type: "FeatureCollection",
            features:  [] as GeoJSON.Feature[]        
        };

        if (geoFocus === "US") {

            if (loadedStatesGeoData) {
                featuresData.features = statesGeoData.features.map((d: GeoJSON.Feature) => {
                    return { ...d, properties: { ...d.properties, selected: true } };
                });

                setGeoJsonData(featuresData);
                setLoadedGeoJsonData(true);
                setLoadedGeoJsonBoundaryData(false);
            }

        } else if (geoFocus === "State") {
            setVisualizationLoading(true);
            if (loadedCountiesLongData) {
                featuresData.features = partOfSelectedState(countiesData, selectedState.stfp);
                boundaryData.features = partOfSelectedState(statesGeoData, selectedState.stfp);

                setGeoJsonData(featuresData);
                setLoadedGeoJsonData(true);
                setGeoJsonBoundaryData(boundaryData);
                setLoadedGeoJsonBoundaryData(true);
                setPointsData(countiesData.features);
                setLoadedPointsData(true);
                setVisualizationLoading(false);
            }

        } else if (geoFocus === "County") {
            setVisualizationLoading(true);
            if (loadedPollingLocsData && loadedCountiesData && loadedTractsData && loadedVdData) {

                featuresData.features = partOfSelectedCounty(tractsData, selectedCounty.cntyfp);
                boundaryData.features = partOfSelectedCounty(countiesData, selectedCounty.cntyfp);
                vdfc.features = partOfSelectedCounty(vdData, selectedCounty.cntyfp);

                setGeoJsonData(featuresData);
                setLoadedGeoJsonData(true);
                setGeoJsonBoundaryData(boundaryData);
                setLoadedGeoJsonBoundaryData(true);
                setGeoJsonVdData(vdfc);
                setPointsData(pollingLocsData);
                setLoadedPointsData(true);
                setVisualizationLoading(false);
            }
        }

    }, [geoFocus, selectedState, selectedCounty, loadedStatesGeoData, loadedCountiesData, 
        loadedPollingLocsData, loadedTractsData, loadedVdData]);

    return(
        <>
            <BrowserMessage />
            <OverlayScreen />
            {visualizationLoading ? <Loader loading={visualizationLoading}/> : <> </>}
            <Main initialized={initialized}>
                <Status geoFocus={geoFocus} equityIndicator={equityIndicator} showEquityIndicator={showEquityIndicator}
                        indicator={indicator} pollHover={pollHover} geoHover={geoHover} showPolls={showPolls} changeYear={changeYear} 
                        selectedCounty={selectedCounty} selectedState={selectedState} countiesData={countiesData} loadedCountiesData={loadedCountiesData}/>
                {geoFocus !== "US" ? <QueryMenu geoFocus={geoFocus} setGeoFocus={setGeoFocus} changeYear={changeYear} setChangeYear={setChangeYear} 
                        changeYearOpts={changeYearOpts}
                        selectedState={selectedState} setSelectedState={setSelectedState} 
                        selectedCounty={selectedCounty} setSelectedCounty={setSelectedCounty} 
                        statesData={statesGeoData}
                        countiesData={countiesData}
                        equityIndicator={equityIndicator} setEquityIndicator={setEquityIndicator}
                        showVD={showVD} setShowVD={setShowVD} showPolls={showPolls} setShowPolls={setShowPolls} 
                        showCensusTracts={showCensusTracts} setShowCensusTracts={setShowCensusTracts} 
                        showEquityIndicator={showEquityIndicator} setShowEquityIndicator={setShowEquityIndicator}
                        indicator={indicator} setIndicator={setIndicator} loadedCountiesData={loadedCountiesData}
                        pctBlackCVAPCountyFilter={pctBlackCVAPCountyFilter} setPctBlackCVAPCountyFilter={setPctBlackCVAPCountyFilter} 
                        pctBlackCVAPRangeCountyMarks={pctBlackCVAPRangeCountyMarks}
                        pctHispanicCVAPCountyFilter={pctHispanicCVAPCountyFilter} setPctHispanicCVAPCountyFilter={setPctHispanicCVAPCountyFilter} 
                        pctHispanicCVAPRangeCountyMarks={pctHispanicCVAPRangeCountyMarks}
                        popDensityCountyFilter={popDensityCountyFilter} setPopDensityCountyFilter={setPopDensityCountyFilter} 
                        popDensityRangeCountyMarks={popDensityRangeCountyMarks}

                        changeNoPollsCountyFilter={changeNoPollsCountyFilter} setChangeYearCVAPPerPollCountyFilter={setChangeYearCVAPPerPollCountyFilter} 
                        changeNoPollsRangeCountyMarks={changeNoPollsRangeCountyMarks}
                        overallChangeCountyFilter={overallChangeCountyFilter} setOverallChangeCountyFilter={setOverallChangeCountyFilter} 
                        overallChangeRangeCountyMarks={overallChangeRangeCountyMarks}
                        changeYearCVAPPerPollCountyFilter={changeYearCVAPPerPollCountyFilter} setChangeNoPollsCountyFilter={setChangeNoPollsCountyFilter} 
                        changeYearCVAPPerPollCountyMarks={changeYearCVAPPerPollCountyMarks}
                        baseYearCVAPPerPollCountyFilter={baseYearCVAPPerPollCountyFilter} setBaseYearCVAPPerPollCountyFilter={setBaseYearCVAPPerPollCountyFilter} 
                        baseYearCVAPPerPollCountyMarks={baseYearCVAPPerPollCountyMarks}

                        pctBlackCVAPTractFilter={pctBlackCVAPTractFilter} setPctBlackCVAPTractFilter={setPctBlackCVAPTractFilter} 
                        pctBlackCVAPRangeTractMarks={pctBlackCVAPRangeTractMarks}
                        pctHispanicCVAPTractFilter={pctHispanicCVAPTractFilter} setPctHispanicCVAPTractFilter={setPctHispanicCVAPTractFilter} 
                        pctHispanicCVAPRangeTractMarks={pctHispanicCVAPRangeTractMarks}
                        popDensityTractFilter={popDensityTractFilter} setPopDensityTractFilter={setPopDensityTractFilter} 
                        popDensityRangeTractMarks={popDensityRangeTractMarks}

                        changeNoPollsTractFilter={changeNoPollsTractFilter} setChangeNoPollsTractFilter={setChangeNoPollsTractFilter} 
                        changeNoPollsRangeTractMarks={changeNoPollsRangeTractMarks}
                        overallChangeTractFilter={overallChangeTractFilter} setOverallChangeTractFilter={setOverallChangeTractFilter} 
                        overallChangeRangeTractMarks={overallChangeRangeTractMarks}
                        
                        /> : <></>}
                <Map mapRef={mapRef} geoFocus={geoFocus} setGeoFocus={setGeoFocus}
                        selectedState={selectedState} setSelectedState={setSelectedState} 
                        selectedCounty={selectedCounty} setSelectedCounty={setSelectedCounty} 
                        showPolls={showPolls} showVD={showVD} showEquityIndicator={showEquityIndicator}
                        showCensusTracts={showCensusTracts}
                        setPollHover={setPollHover} changeYear={changeYear} equityIndicator={equityIndicator} 
                        setGeoHover={setGeoHover} 
                        pollingLocsData={pollingLocsData} countiesData={countiesData} tractsData={tractsData}
                        vdData={vdData} statesData={statesGeoData}
                        loadedCountiesData={loadedCountiesData} loadedTractsData={loadedTractsData} loadedVdData={loadedVdData} loadedPollingLocsData={loadedPollingLocsData}
                        indicator={indicator} loadedStatesData={loadedStatesGeoData}
                        pctBlackCVAPCountyFilter={pctBlackCVAPCountyFilter} pctHispanicCVAPCountyFilter={pctHispanicCVAPCountyFilter} popDensityCountyFilter={popDensityCountyFilter}
                        pctBlackCVAPTractFilter={pctBlackCVAPTractFilter} pctHispanicCVAPTractFilter={pctHispanicCVAPTractFilter} popDensityTractFilter={popDensityTractFilter}
                        changeNoPollsCountyFilter={changeNoPollsCountyFilter} overallChangeCountyFilter={overallChangeCountyFilter}
                        changeYearCVAPPerPollCountyFilter={changeYearCVAPPerPollCountyFilter} baseYearCVAPPerPollCountyFilter={baseYearCVAPPerPollCountyFilter}
                        changeNoPollsTractFilter={changeNoPollsTractFilter} overallChangeTractFilter={overallChangeTractFilter}
                        setVisualizationLoading={setVisualizationLoading}/>
            </Main>
        </>
    )
}
